<template>
  <div class="flyover-videos-app">
    <!-- Header (matches the recordings / planned-hikes list header) -->
    <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
      <div>
        <h1 class="fv-heading mb-1">{{ heading }}</h1>
        <p v-if="isPublic && publicDisplayName" class="fv-subtitle mb-0"><small>{{ $t('flyover_videos.created_by_public_display_name', { public_display_name: publicDisplayName }) }}</small></p>
      </div>
    </div>

    <!-- Search row -->
    <div class="search-row d-flex gap-3 mb-4">
      <div class="search-input-wrapper flex-grow-1">
        <i class="material-icons search-icon">search</i>
        <input
          v-model="searchInput"
          class="form-control search-input"
          type="text"
          :placeholder="$t('flyover_videos.search_by_recording_name')"
          @keyup.enter="applySearch"
        />
      </div>
    </div>

    <!-- Filters row -->
    <div class="filters-row d-flex gap-2 mb-3">
      <button
        v-for="opt in orientationOptions"
        :key="opt.value"
        type="button"
        class="btn btn-sm btn-filter rounded-pill"
        :class="{ active: orientation === opt.value }"
        @click="setOrientation(opt.value)"
      >
        {{ opt.label }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading && videos.length === 0" class="text-center py-5">
      <div class="spinner-border text-light" role="status"><span class="visually-hidden">{{ $t('shared.loading') }}</span></div>
    </div>

    <!-- Empty state -->
    <div v-else-if="!loading && videos.length === 0" class="fv-empty">
      <span class="material-icons">movie</span>
      <p class="mb-2">{{ emptyStateMessage }}</p>
      <p v-if="!isPublic && !hasQuery && orientation === 'ALL'" class="fv-empty__hint">
        <small>{{ $t('flyover_videos.open_a_recording_s_3d_flyover') }}</small>
      </p>
    </div>

    <!-- List -->
    <div v-else>
      <div class="fv-list">
        <FlyoverVideoCard
          v-for="video in videos"
          :key="video.id"
          :video="video"
          :can-delete="!isPublic"
          @delete="confirmDelete"
          @share="(v) => shareVideo(v)"
          @open="openFullscreen"
        />
      </div>

      <div v-if="hasNextPage" class="see-more d-flex justify-content-center m-0">
        <button class="btn btn-link" :disabled="loading" @click="loadMore">
          {{ loading ? $t('shared.loading') : $t('flyover_videos.see_more') }}
        </button>
      </div>

      <div v-if="totalCount > 0" class="fv-count">
        <small>{{ totalCountMessage }}</small>
      </div>
    </div>

    <!-- Fullscreen video overlay (opened from the preview / title) -->
    <div v-if="fullscreenVideo" class="fv-fullscreen" @click.self="closeFullscreen">
      <button type="button" class="fv-fullscreen__close" :aria-label="$t('shared.close')" @click="closeFullscreen">
        <span class="material-icons">close</span>
      </button>
      <!-- Same share wizard the row's share icon opens, reported as the player (my_flyovers_player). -->
      <button
        v-if="canShareFromPlayer()"
        type="button"
        class="fv-fullscreen__share"
        :aria-label="$t('shared.share')"
        @click="shareFromFullscreen"
      >
        <span class="material-icons">share</span>
      </button>
      <video
        ref="fullscreenVideoEl"
        :src="fullscreenVideo.url"
        :class="['fv-fullscreen__video', isVertical(fullscreenVideo.aspect) ? 'is-vertical' : 'is-horizontal']"
        controls
        autoplay
        playsinline
      ></video>
    </div>

    <!-- Toast -->
    <div v-if="toast" class="fv-toast">{{ toast }}</div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import FlyoverVideoCard from './components/FlyoverVideoCard.vue';
import { i18n } from '../../i18n';
import { ORIENTATION_ALL, ORIENTATION_VERTICAL, ORIENTATION_HORIZONTAL, isVertical } from './components/formatters';
import { buildReadyVideoShareConfig, flyoverShareOpener, myFlyoversShareContext } from '../flyover_share/readyVideoShare';

const props = defineProps({
  // 'current' (logged-in user's own list, deletable) or 'public' (another user's profile)
  context: { type: String, default: 'current' },
  firebaseUserId: { type: String, default: null },
  publicDisplayName: { type: String, default: null },
});

const isPublic = computed(() => props.context === 'public');
const t = (...a) => i18n.global.t(...a);

const heading = computed(() => (isPublic.value ? t('flyover_videos.flyovers') : t('flyover_videos.my_flyovers')));

const PER_PAGE = 20;
const videos = ref([]);
const loading = ref(false);
const currentPage = ref(1);
const totalCount = ref(0);
const hasNextPage = ref(false);
const orientation = ref(ORIENTATION_ALL);
const searchInput = ref('');
const appliedQuery = ref('');
const toast = ref('');
let toastTimer = null;
const fullscreenVideo = ref(null);
const fullscreenVideoEl = ref(null);

const hasQuery = computed(() => appliedQuery.value.trim().length > 0);

// Order matches the app: All / Landscape / Vertical.
const orientationOptions = [
  { value: ORIENTATION_ALL, get label() { return t('flyover_videos.all'); } },
  { value: ORIENTATION_HORIZONTAL, get label() { return t('flyover_videos.landscape'); } },
  { value: ORIENTATION_VERTICAL, get label() { return t('flyover_videos.vertical'); } },
];

const emptyStateMessage = computed(() => {
  if (hasQuery.value) return t('flyover_videos.no_videos_match_search');
  if (orientation.value !== ORIENTATION_ALL) return t('flyover_videos.no_videos_in_orientation');
  return isPublic.value ? t('flyover_videos.user_has_no_videos') : t('flyover_videos.you_have_no_videos');
});

const totalCountMessage = computed(() => {
  return totalCount.value === 1
    ? t('flyover_videos.one_flyover_video')
    : t('flyover_videos.count_flyover_videos', { count: totalCount.value });
});

const FIELDS = `
  flyoverVideos {
    id
    url
    aspect
    fileSizeBytes
    durationSeconds
    createdAt
    renderKind
    recordingTitle
    recordingShortAddress
    recordingObfuscatedSlug
    hikeTitle
    hikeUrl
    hikeShortAddress
  }
  totalCount
  currentPage
  hasNextPage
`;

// includeTrailers opts this (updated) client into Trail Trailer rows — the server keeps them out of
// shipped clients' lists, which render only the recording fields.
function listQuery() {
  if (isPublic.value) {
    return `query PublicUserFlyoverVideos($firebaseUserId: String!, $orientation: FlyoverVideoAspectEnum, $query: String, $page: Int, $perPage: Int) {
      publicUserFlyoverVideos(firebaseUserId: $firebaseUserId, orientation: $orientation, query: $query, page: $page, perPage: $perPage, includeTrailers: true) { ${FIELDS} }
    }`;
  }
  return `query UserFlyoverVideos($orientation: FlyoverVideoAspectEnum, $query: String, $page: Int, $perPage: Int) {
    userFlyoverVideos(orientation: $orientation, query: $query, page: $page, perPage: $perPage, includeTrailers: true) { ${FIELDS} }
  }`;
}

const DESTROY_MUTATION = `mutation DestroyFlyoverVideo($input: DestroyFlyoverVideoInput!) {
  destroyFlyoverVideo(input: $input) { id }
}`;

async function postGraphql(query, variables) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  const response = await fetch('/graphql', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
    body: JSON.stringify({ query, variables }),
  });
  return response.json();
}

async function fetchVideos(page = 1) {
  loading.value = true;
  try {
    const variables = { page, perPage: PER_PAGE };
    if (orientation.value !== ORIENTATION_ALL) variables.orientation = orientation.value;
    if (hasQuery.value) variables.query = appliedQuery.value.trim();
    if (isPublic.value) variables.firebaseUserId = props.firebaseUserId;

    const result = await postGraphql(listQuery(), variables);
    const root = isPublic.value ? 'publicUserFlyoverVideos' : 'userFlyoverVideos';
    const data = result.data?.[root];
    if (data) {
      const rows = data.flyoverVideos || [];
      videos.value = page === 1 ? rows : videos.value.concat(rows);
      totalCount.value = data.totalCount || 0;
      currentPage.value = data.currentPage || 1;
      hasNextPage.value = data.hasNextPage || false;
    }
  } catch (error) {
    console.error('Error fetching flyover videos:', error);
  } finally {
    loading.value = false;
  }
}

function setOrientation(value) {
  if (orientation.value === value) return;
  orientation.value = value;
  fetchVideos(1);
}

function applySearch() {
  const next = searchInput.value.trim();
  if (next === appliedQuery.value) return;
  appliedQuery.value = next;
  fetchVideos(1);
}

function loadMore() {
  if (!loading.value && hasNextPage.value) fetchVideos(currentPage.value + 1);
}

function showToast(message) {
  toast.value = message;
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { toast.value = ''; }, 2500);
}

// Share opens the FlyoverShare wizard straight on its "ready" step for this video (the share sheet
// with Save / Copy link / social targets), matching the recording-page share experience. The row's
// share icon and the fullscreen player both come through here and differ only in `source`; `dims` is
// the player's decoded video size, the format fallback for a row without an aspect.
function shareVideo(video, source = 'my_flyovers', dims = null) {
  if (!video.url) return;
  const show = flyoverShareOpener();
  if (show) {
    show(buildReadyVideoShareConfig(video.url, myFlyoversShareContext(video, source), dims));
    return;
  }
  // Fallback if the share app isn't mounted: copy the link.
  navigator.clipboard?.writeText(video.url).then(
    () => showToast(t('flyover_videos.link_copied')),
    () => showToast(t('flyover_videos.could_not_share_video')),
  );
}

function openFullscreen(video) {
  if (!video.url) return;
  fullscreenVideo.value = video;
}

function closeFullscreen() {
  fullscreenVideo.value = null;
}

// Evaluated per render rather than once: the wizard registers its global when its own app mounts.
function canShareFromPlayer() {
  return !!flyoverShareOpener();
}

// The overlay closes first — it sits above the wizard's modal, which would otherwise open behind it.
function shareFromFullscreen() {
  const video = fullscreenVideo.value;
  if (!video) return;
  const el = fullscreenVideoEl.value;
  const dims = el ? { width: el.videoWidth, height: el.videoHeight } : null;
  closeFullscreen();
  shareVideo(video, 'my_flyovers_player', dims);
}

function onKeydown(e) {
  if (e.key === 'Escape') closeFullscreen();
}

async function confirmDelete(video) {
  if (isPublic.value) return; // safety: delete is owner-only
  if (!window.confirm(t('flyover_videos.confirm_delete_video'))) return;
  try {
    const result = await postGraphql(DESTROY_MUTATION, { input: { id: video.id } });
    if (result.errors?.length) {
      showToast(t('flyover_videos.could_not_delete_video'));
      return;
    }
    videos.value = videos.value.filter((v) => v.id !== video.id);
    totalCount.value = Math.max(0, totalCount.value - 1);
    showToast(t('flyover_videos.video_deleted'));
  } catch (error) {
    console.error('Error deleting flyover video:', error);
    showToast(t('flyover_videos.could_not_delete_video'));
  }
}

onMounted(() => {
  fetchVideos(1);
  window.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
/* Sits on the (dark) profile page like the recordings / planned-hikes lists — no panel wrapper. */
.flyover-videos-app {
  color: #fff;
}
/* Quieter than display-4 — a calm section header, not a shouty hero title. */
.fv-heading {
  font-size: 1.35rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  margin: 0;
}
.fv-subtitle {
  color: rgba(255, 255, 255, 0.45);
  font-size: 13px;
}

/* Filters row + pills — copied from the recordings list so it reads as the same family. */
.filters-row {
  flex-wrap: wrap;
}
.btn-filter {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.8);
  padding: 0.35rem 0.9rem;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
}
.btn-filter:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
}
.btn-filter.active {
  background: #2b2c34;
  border-color: #fbcd01;
  color: #fff;
}

/* Search input — copied from the recordings list. */
.search-input-wrapper {
  position: relative;
}
.search-icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 18px;
  pointer-events: none;
}
.search-input {
  background: #1f2026;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  padding: 0.5rem 1rem 0.5rem 2.75rem;
  border-radius: 118px;
  font-size: 13px;
  height: 40px;
}
.search-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.2);
  background: #252530;
  box-shadow: none;
}
.search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.fv-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.fv-empty {
  text-align: center;
  padding: 3.5rem 2rem;
  color: rgba(255, 255, 255, 0.6);
}
.fv-empty .material-icons {
  font-size: 64px;
  color: rgba(255, 255, 255, 0.2);
  margin-bottom: 1rem;
}
.fv-empty__hint {
  color: rgba(255, 255, 255, 0.4);
}

.see-more {
  margin-top: 1.5rem;
}
.see-more .btn-link {
  color: #fbcd01;
  text-decoration: none;
  font-weight: 600;
}
.see-more .btn-link:hover {
  text-decoration: underline;
}
.fv-count {
  margin-top: 1.25rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
}

.fv-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 2050;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.fv-fullscreen__video {
  background: #000;
  border-radius: 8px;
  max-width: 100%;
  max-height: 100%;
}
.fv-fullscreen__video.is-vertical {
  height: 92vh;
  width: auto;
  aspect-ratio: 9 / 16;
}
.fv-fullscreen__video.is-horizontal {
  width: min(100%, 1400px);
  height: auto;
  aspect-ratio: 16 / 9;
}
.fv-fullscreen__close {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.fv-fullscreen__close:hover {
  background: rgba(255, 255, 255, 0.22);
}
/* Immediately left of close, same treatment. */
.fv-fullscreen__share {
  position: fixed;
  top: 20px;
  right: 72px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.fv-fullscreen__share:hover {
  background: rgba(255, 255, 255, 0.22);
}

.fv-toast {
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 10px 20px;
  border-radius: 999px;
  z-index: 2000;
}
</style>
